import styles from './CTAFilled.module.scss';

import { Colors } from '../../../../theme/Colors';
import { CTATextContent } from '../CTATextContent';
import { BgColor, DeprecatedBgColor, CTABtnColor } from '../types';
import { getBgColor } from '../utils';
import type { AnchorTarget } from '../../../../ts-types';
import { LegacyColorMap } from '@/theme/LegacyColorMap';

export type Props = {
  /**
   * Sets the container's background color, must be one of type `'lava60' | 'ocean40' | 'sun60' | 'white'
   * | 'zapier' | 'night' | 'moss' | 'neutral800' | 'neutral100' | 'earth'`.
   */
  backgroundColor?: BgColor | DeprecatedBgColor;
  /**
   * max-width in pixels applied to the content area of container.
   */
  maxWidth?: number;
  /**
   * Heading text content.
   */
  heading: React.ReactNode;
  /**
   * Text content displayed below the heading.
   */
  subHeading?: React.ReactNode;
  /**
   * Text content displayed below the sub-heading.
   */
  description?: React.ReactNode;
  /**
   * Text content of the CTA button.
   */
  ctaText?: React.ReactNode;
  /**
   * href for the CTA button.
   */
  ctaHref?: string;
  /**
   * Color of the CTA button.
   *
   * Must be one of the type `'primary' | 'primary-alt' | 'primary-dark' | 'brand'`
   */
  ctaBtnColor?: CTABtnColor;
  /**
   * CTA button click handler.
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
  /**
   * CTA button keyDown handler.
   */
  onCtaKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  /**
   * Renders in the space opposite the text.
   * Can be used to render media such as images, videos, or any other React Component.
   */
  children?: React.ReactNode;
  /**
   * The `target` attribute for the CTA button.
   */
  ctaTarget?: AnchorTarget;
};

/**
 * `CTAFilled` is used to direct our customers to take a specific action. It features a heading, sub-heading, description, call-to-action button, and media, which is rendered as `children`.
 */
export const CTAFilled = ({
  backgroundColor = 'neutral100',
  children,
  ctaHref,
  ctaBtnColor,
  ctaText,
  description,
  heading,
  maxWidth = 1170,
  onCtaClick,
  onCtaKeyDown,
  subHeading,
  ctaTarget,
}: Props) => {
  const bgColor = getBgColor(backgroundColor);

  return (
    <div
      className={styles.root}
      style={{
        '--_zds-cta-filled-max-width': `${maxWidth}px`,
        '--_zds-cta-filled-bg-color': Colors[LegacyColorMap[bgColor]],
      }}
      data-zds
    >
      <CTATextContent
        heading={heading}
        subHeading={subHeading}
        description={description}
        ctaText={ctaText}
        ctaHref={ctaHref}
        ctaBtnColor={ctaBtnColor}
        onCtaClick={onCtaClick}
        onCtaKeyDown={onCtaKeyDown}
        bgColor={bgColor}
        ctaTarget={ctaTarget}
      />
      <div className={styles.mediaContent} data-testid="media-content" data-zds>
        {children}
      </div>
    </div>
  );
};
